import { styled } from '@mui/material/styles';
import { useEffect, useMemo, useState } from 'react';
import rubyplayLogo from '../../assets/rubyplay-logo.png';

type ResilientImageProps = {
  src?: string | null;
  alt: string;
  'aria-label'?: string;
  className?: string;
};

const ResilientImage = ({
  src,
  alt,
  className,
  'aria-label': ariaLabel,
}: ResilientImageProps) => {
  const normalizedSrc = useMemo(() => {
    const trimmed = typeof src === 'string' ? src.trim() : '';
    return trimmed ? trimmed : null;
  }, [src]);

  const [status, setStatus] = useState<
    'loading' | 'loaded' | 'error'
  >(normalizedSrc ? 'loading' : 'error');

  useEffect(() => {
    if (!normalizedSrc) {
      setStatus('error');
      return;
    }

    let cancelled = false;
    setStatus('loading');

    const img = new Image();
    img.onload = () => {
      if (!cancelled) setStatus('loaded');
    };
    img.onerror = () => {
      if (!cancelled) setStatus('error');
    };
    img.src = normalizedSrc;

    return () => {
      cancelled = true;
    };
  }, [normalizedSrc]);

  if (status !== 'loaded' || !normalizedSrc) {
    return (
      <Placeholder
        className={className}
        aria-label={ariaLabel ?? alt}
        role="img"
      >
        <FallbackLogo src={rubyplayLogo} alt="" aria-hidden="true" />
      </Placeholder>
    );
  }

  return (
    <StyledImg
      className={className}
      src={normalizedSrc}
      alt={alt}
      aria-label={ariaLabel}
      onError={() => setStatus('error')}
    />
  );
};

const Placeholder = styled('div')(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor:
    (theme.palette as unknown as { grey200?: string }).grey200 ??
    theme.palette.grey[700],
  flexShrink: 0,
}));

const FallbackLogo = styled('img')({
  width: '70%',
  height: '70%',
  objectFit: 'contain',
  display: 'block',
});

const StyledImg = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  flexShrink: 0,
  display: 'block',
});

export default ResilientImage;
